import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { applyBranding, fetchBranding, footerLine, initialBranding, logoUrl, type Branding } from "../lib/branding";

/** The organisation's brand from Branding Settings; until the server answers, the last good brand remembered in
 * this browser, else the OneSuite defaults. */
export function useBranding(): Branding {
  const q = useQuery({
    queryKey: ["branding"],
    queryFn: () => fetchBranding(),
    placeholderData: initialBranding(),
    staleTime: 300_000,
  });
  return q.data ?? initialBranding();
}

/** The organisation's logo: `light` for light surfaces, `dark` for dark ones, `mark` for the square icon.
 * `decorative` (next to a text label, or a loading mark) gives it an empty alt. */
export function BrandLogo({ variant = "light", className, decorative = false }: {
  variant?: "light" | "dark" | "mark"; className?: string; decorative?: boolean;
}) {
  const b = useBranding();
  return <img src={logoUrl(b, variant)} alt={decorative ? "" : b.organisation_name} className={className} />;
}

/** Keeps the document's brand colours, title, favicon and theme colour in step with the brand. Renders nothing. */
export function BrandingEffect() {
  const b = useBranding();
  useEffect(() => { applyBranding(b); }, [b]);
  return null;
}

/** A11 footer line (legal footer, else the organisation name); nothing while the settings are empty. */
export function BrandFooterLine({ className }: { className?: string }) {
  const line = footerLine(useBranding());
  if (line === null) return null;
  return <span data-brand-footer className={className}>{line}</span>;
}
